import { Form } from "antd"
import cs from "./index.module.less"
import cx from "clsx"
import { Group, notification } from "components/common"
import PaymentProofImageUpload from "../paymentProofImageUpload"
import FormItemCreator from "../formItemCreator"
import { useSelector } from "react-redux"
import { t } from "i18next"
import DepositSlipSelect from "../depositSlipSelect"
import { useCallback, useEffect, useMemo } from "react"
import {
  cardPaymentFormChildren,
  cardPaymentFormProps,
  chequeFormChildren,
  chequeFormProps,
  depositSlipFormChildren,
  depositSlipFormProps,
  onlinePaymentFormChildren,
  onlinePaymentFormProps,
  payorderFormChildren,
  payorderFormProps,
} from "features/leads/components/addSalesModal/utils/formItemConstants"
import { fieldMapper, shouldRenderFormItem } from "features/leads/components/addSalesModal/utils"
import { useAddSalesContext } from "features/leads/components/addSalesModal/addSalesContext/addSalesContext"
import { debounce } from "lodash"

const PaymentProofFormItems = ({ rules }) => {
  const userPlateformKey = useSelector(state => state.app.user?.info?.AgencyConfiguration?.Platform?.platformKey)

  const { addSalesApis, form, stateManager, formWatchers } = useAddSalesContext()
  const { project } = formWatchers
  const { leadSalesInfo, referenceError, modeOfPayments, banks, validateReference, referenceValidity, paymentMode } =
    addSalesApis

  const { getFieldValue, setFieldsValue, validateFields } = form

  const depositSlip = Form.useWatch("bank_deposit_slip", form)
  const onlinePayment = Form.useWatch("online_payment_screenshot", form)
  const cheque = Form.useWatch("cheque", form)
  const cardPayment = Form.useWatch("receipt", form)
  const modeOfPayment = Form.useWatch("modeOfPayment", form)
  const payorderBank = Form.useWatch("payorderBank", form)
  const chequeBank = Form.useWatch("chequeBank", form)

  const optionList = useMemo(() => {
    return {
      bank: banks,
      paymentMode: paymentMode,
    }
  }, [banks, paymentMode])

  useEffect(() => {
    if (cheque) {
      const chequeField = modeOfPayments?.find(item => item.key === "cheque")?.cheque
      fieldMapper(cheque?.imageData, chequeField, form)
      if (cheque?.imageData?.chequeNumber) {
        validateReference({
          referenceNumber: cheque?.imageData?.chequeNumber,
          bankKey: getFieldValue("chequeBank"),
          paymentModeKey: getFieldValue("modeOfPayment"),
        })
          .unwrap()
          .then(e => {
            validateFields(["referenceNumber"])
          })
      }
    }
    if (onlinePayment) {
      const onlinePaymentField = modeOfPayments?.find(item => item.key === "online_payment")?.onlinePayment
      fieldMapper(onlinePayment?.imageData, onlinePaymentField, form)
    }
  }, [cheque, onlinePayment, modeOfPayments])

  useEffect(() => {
    if (depositSlip) {
      const depositSlipField = modeOfPayments?.find(item =>
        ["cheque", "cash", "bank_order"].includes(item.key)
      )?.depositSlip
      fieldMapper(depositSlip?.imageData, depositSlipField, form)
    }
  }, [depositSlip])

  useEffect(() => {
    if (cardPayment) {
      const cardPaymentField = modeOfPayments?.find(item => item.key === "card_payment")?.cardPayment
      fieldMapper(cardPayment?.imageData, cardPaymentField, form)
    }
  }, [cardPayment])

  const debouncedReferenceValidator = useCallback(
    debounce(value => {
      validateReference(value)
        .unwrap()
        .then(e => {
          validateFields(["referenceNumber"])
        })
        .catch(e => notification?.error({ message: e?.error }))
    }, 500),
    []
  )

  const formItemCreaterObject = {
    1: {
      conditionToCheckRender: shouldRenderFormItem(cardPayment, project, stateManager, leadSalesInfo),
      extraCondition: modeOfPayment === "card_payment",
      formItemPropList: cardPaymentFormProps(),
      formItemChildrenList: cardPaymentFormChildren({ setFieldsValue }),
    },
    5: {
      extraCondition: true,
      conditionToCheckRender: true,
      formItemPropList: payorderFormProps({
        referenceError,
        referenceValidity,
      }),
      formItemChildrenList: payorderFormChildren({
        setFieldsValue,
        optionList,
        debouncedReferenceValidator,
        payorderBank,
      }),
    },
    2: {
      extraCondition: true,
      conditionToCheckRender: shouldRenderFormItem(depositSlip, project, stateManager, leadSalesInfo),
      formItemPropList: depositSlipFormProps(),
      formItemChildrenList: depositSlipFormChildren({
        setFieldsValue,
        optionList,
      }),
    },
    6: {
      extraCondition: true,
      conditionToCheckRender: shouldRenderFormItem(onlinePayment, project, stateManager, leadSalesInfo),
      formItemPropList: onlinePaymentFormProps(userPlateformKey),
      formItemChildrenList: onlinePaymentFormChildren({
        setFieldsValue,
        optionList,
      }),
    },
    3: {
      extraCondition: true,
      conditionToCheckRender: shouldRenderFormItem(cheque, project, stateManager, leadSalesInfo),
      formItemPropList: chequeFormProps({
        referenceError,
        referenceValidity,
      }),
      formItemChildrenList: chequeFormChildren({
        setFieldsValue,
        optionList,
        debouncedReferenceValidator,
        chequeBank,
        modeofPayment: modeOfPayment,
      }),
    },
  }

  return (
    <Form.Item noStyle shouldUpdate={true}>
      {({ getFieldValue }) => {
        if (!!getFieldValue("modeOfPayment")) {
          return modeOfPayments
            ?.find(item => item?.key === modeOfPayment)
            ?.PaymentModeAttachmentTypeMappings?.map(e => (
              <Form.Item
                className={cx(cs.highlight, cs.highlightSecondary, "mbe-0 span-all")}
                key={e.AttachmentType.key}
              >
                <Group template="auto 1fr">
                  <Form.Item
                    name={e.AttachmentType.key}
                    label={e.AttachmentType.title}
                    required={e.mandatory}
                    rules={[
                      {
                        validator: async (_, value) => {
                          if (!!!value && e.mandatory) {
                            return Promise.reject(t("This Attachment is Required"))
                          }
                          return Promise.resolve()
                        },
                      },
                    ]}
                  >
                    <div>
                      {e.attachmentTypeId !== 2 ? (
                        <PaymentProofImageUpload
                          setFiles={data => {
                            form.setFieldsValue({ [e.AttachmentType.key]: data })
                          }}
                          paymentProofProps={{
                            attachmentTypeId: e.attachmentTypeId,
                            api: e?.visionRequired,
                            visionAllowedonProject:
                              stateManager?.project?.paymentProof ||
                              project?.paymentProof ||
                              leadSalesInfo?.project?.paymentProof,
                          }}
                          platformKey={userPlateformKey}
                          projectId={project?.id || stateManager?.project?.projectId || leadSalesInfo?.projectId}
                        />
                      ) : (
                        <DepositSlipSelect
                          setFiles={data => {
                            form.setFieldsValue({ [e.AttachmentType.key]: data })
                          }}
                          paymentProofProps={{
                            attachmentTypeId: e.attachmentTypeId,
                            api: e?.visionRequired,
                            visionAllowedonProject:
                              stateManager?.project?.paymentProof ||
                              project?.paymentProof ||
                              leadSalesInfo?.project?.paymentProof,
                          }}
                          platformKey={userPlateformKey}
                          project={project ?? stateManager?.project}
                        />
                      )}
                    </div>
                  </Form.Item>
                  {formItemCreaterObject[e?.attachmentTypeId]?.conditionToCheckRender &&
                    formItemCreaterObject[e?.attachmentTypeId]?.extraCondition && (
                      <>
                        <FormItemCreator
                          template="repeat(2, 1fr)"
                          formItemPropList={formItemCreaterObject[e?.attachmentTypeId]?.formItemPropList}
                          formItemChildrenList={formItemCreaterObject[e?.attachmentTypeId]?.formItemChildrenList}
                          rules={rules}
                        />
                      </>
                    )}
                </Group>
              </Form.Item>
            ))
        }
      }}
    </Form.Item>
  )
}
export default PaymentProofFormItems
